/* SEQ Air Care - Regional hub landing */
'use strict';

(() => {
  const ROOT_SELECTOR = '[data-seq-region-hub]';
  const BOOKING_CTA_TEXT = 'See Available Times';
  const BOOKING_LINK_FRAGMENT = '/products/split-system-deep-clean';

  function normalise(value) {
    return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function escapeRegExp(value) {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function removeDuplicateThemeTitle(root) {
    const region = (root.dataset.region || '').trim();
    if (!region) return;

    const allowed = new Set([
      normalise(region),
      normalise(`Air Conditioner Cleaning ${region}`),
      normalise(`Air Con Cleaning ${region}`),
      normalise(`Split System Air Conditioner Cleaning ${region}`),
      normalise(`Split System Air Con Cleaning ${region}`)
    ]);

    document
      .querySelectorAll('h1.main-page-title, .main-page-title, h1.page-title, .page-title, main > h1')
      .forEach((heading) => {
        if (root.contains(heading)) return;
        if (!allowed.has(normalise(heading.textContent))) return;
        heading.hidden = true;
        heading.setAttribute('aria-hidden', 'true');
        heading.style.setProperty('display', 'none', 'important');

        const wrapper = heading.parentElement;
        if (wrapper && wrapper.children.length === 1 && !wrapper.matches('header, nav, main, body')) {
          wrapper.style.setProperty('display', 'none', 'important');
        }
      });
  }

  function getRegionSuburbs(root) {
    return String(root.dataset.regionSuburbs || '')
      .split('|')
      .map((item) => item.trim())
      .filter(Boolean)
      .sort((a, b) => b.length - a.length);
  }

  function findRecentJobLocation(card, root) {
    const text = card?.textContent?.replace(/\s+/g, ' ').trim() || '';
    const suburb = getRegionSuburbs(root).find((name) =>
      new RegExp(`\\b${escapeRegExp(name)}\\b`, 'i').test(text)
    );
    return suburb || root.dataset.region || 'Local job';
  }

  function hideHomeJobElement(element) {
    if (!element) return;
    element.classList.add('seq-home-job-card__hidden');
    element.setAttribute('aria-hidden', 'true');
  }

  function setHomeJobButtonLabel(link, label) {
    if (!link) return;
    link.textContent = label;
    link.removeAttribute('aria-hidden');
    link.classList.remove('seq-home-job-card__hidden');
  }

  function normaliseRecentJobCard(card, root) {
    if (!card || card.dataset.regionHomeCardReady === 'true') return;

    const head = card.querySelector('.seq-result-card__head');
    const media = card.querySelector('.seq-ba-slider, [data-seq-ba-slider]');
    let body = card.querySelector('.seq-result-card__body');
    if (!head || !media) return;

    card.dataset.regionHomeCardReady = 'true';
    card.classList.add('is-home-job-card');

    const title = head.querySelector('h3');
    if (title && title.parentElement !== head) head.appendChild(title);

    let location = head.querySelector('.seq-home-job-card__location');
    if (!location) {
      location = document.createElement('span');
      location.className = 'seq-home-job-card__location';
      head.insertBefore(location, title || head.firstChild);
    }
    location.textContent = findRecentJobLocation(card, root);

    Array.from(head.children).forEach((child) => {
      if (child !== location && child !== title) hideHomeJobElement(child);
    });
    Array.from(head.childNodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && node.nodeValue.trim()) node.nodeValue = '';
    });

    if (!body) {
      body = document.createElement('div');
      body.className = 'seq-result-card__body';
      card.appendChild(body);
    }

    const description = card.querySelector('.seq-suburb-job-card__description')
      || Array.from(body.querySelectorAll('p')).find((paragraph) => paragraph.textContent.trim());

    if (description) {
      description.classList.add('seq-home-job-card__description');
      description.classList.remove('seq-home-job-card__hidden');
      description.removeAttribute('aria-hidden');
      if (description.parentElement !== body) body.insertBefore(description, body.firstChild);
    }

    const links = Array.from(card.querySelectorAll('a[href]'));
    const bookingLink = links.find((link) =>
      link.matches('[data-open-booking]') || link.getAttribute('href')?.includes(BOOKING_LINK_FRAGMENT)
    );

    const jobLink = links.find((link) => {
      if (link === bookingLink) return false;
      const label = link.textContent.replace(/\s+/g, ' ').trim();
      return /view (?:the )?(?:full )?job|see what was found|view job/i.test(label);
    }) || links.find((link) => {
      if (link === bookingLink || link.contains(media) || link.contains(head)) return false;
      const href = link.getAttribute('href') || '';
      return /\/(pages|blogs)\//i.test(href) && !href.includes(BOOKING_LINK_FRAGMENT);
    });

    let actions = body.querySelector('.seq-home-job-card__actions');
    if (!actions) {
      actions = document.createElement('div');
      actions.className = 'seq-home-job-card__actions';
      body.appendChild(actions);
    }

    if (jobLink) {
      jobLink.classList.add('seq-home-job-card__button', 'seq-home-job-card__button--job');
      setHomeJobButtonLabel(jobLink, 'View Job');
      actions.appendChild(jobLink);
    }

    let resolvedBookingLink = bookingLink;
    if (!resolvedBookingLink) {
      resolvedBookingLink = document.createElement('a');
      resolvedBookingLink.href = BOOKING_LINK_FRAGMENT;
      resolvedBookingLink.setAttribute('data-open-booking', '');
      resolvedBookingLink.setAttribute('aria-haspopup', 'dialog');
    }

    resolvedBookingLink.setAttribute(
      'data-booking-source',
      `region_hub_${root.dataset.regionSlug || 'local'}_recent_job`
    );
    resolvedBookingLink.classList.add('seq-home-job-card__button', 'seq-home-job-card__button--booking');
    setHomeJobButtonLabel(resolvedBookingLink, BOOKING_CTA_TEXT);
    actions.appendChild(resolvedBookingLink);

    Array.from(body.children).forEach((child) => {
      if (child !== description && child !== actions) hideHomeJobElement(child);
    });
    Array.from(body.childNodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && node.nodeValue.trim()) node.nodeValue = '';
    });

    links.forEach((link) => {
      if (link !== jobLink && link !== resolvedBookingLink) hideHomeJobElement(link);
    });

    Array.from(card.children).forEach((child) => {
      const containsKeptContent = child === head || child === media || child === body
        || child.contains(head) || child.contains(media) || child.contains(body);
      if (!containsKeptContent) hideHomeJobElement(child);
    });
  }

  function initRecentJobs(root) {
    const host = root.querySelector('[data-region-recent-jobs]');
    if (!host) return;

    const normaliseCards = (scope = host) => {
      if (scope.matches?.('.seq-result-card')) normaliseRecentJobCard(scope, root);
      scope.querySelectorAll?.('.seq-result-card').forEach((card) => normaliseRecentJobCard(card, root));
      initJobSliders(scope);
    };

    normaliseCards(host);

    if ('MutationObserver' in window) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) normaliseCards(node);
          });
        });
      });
      observer.observe(host, { childList: true, subtree: true });
    }
  }

  function initJobSliders(scope) {
    const sliders = new Set();
    if (scope.matches?.('[data-seq-ba-slider]')) sliders.add(scope);
    scope.querySelectorAll?.('[data-seq-ba-slider]').forEach((slider) => sliders.add(slider));

    sliders.forEach((slider) => {
      if (slider.dataset.regionSliderReady === 'true') return;
      slider.dataset.regionSliderReady = 'true';

      const range = slider.querySelector('[data-seq-ba-range]');
      const before = slider.querySelector('[data-seq-ba-before]');
      const line = slider.querySelector('[data-seq-ba-line]');
      const beforeImage = slider.querySelector('.seq-ba-image--before');
      if (!range || !before || !line) return;

      const update = () => {
        const value = Math.max(0, Math.min(100, Number(range.value) || 50));
        const width = Math.max(1, Math.round(slider.getBoundingClientRect().width));
        before.style.width = `${value}%`;
        line.style.left = `${value}%`;
        slider.style.setProperty('--seq-slider-width', `${width}px`);
        if (beforeImage) beforeImage.style.width = `${width}px`;
        range.setAttribute('aria-valuetext', `${value}% before image visible`);
      };

      range.addEventListener('input', update, { passive: true });
      range.addEventListener('change', update);

      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(slider);
      } else {
        window.addEventListener('resize', update, { passive: true });
      }

      update();
    });
  }

  function initMobileSticky(root) {
    const sticky = root.querySelector('[data-region-mobile-sticky]');
    const finalSection = root.querySelector('.seq-region-hub__final');
    if (!sticky || !finalSection || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting);
      sticky.classList.toggle('is-hidden', visible);
    }, { threshold: 0.08 });

    observer.observe(finalSection);
  }

  function init(root) {
    if (root.dataset.regionHubReady === 'true') return;
    root.dataset.regionHubReady = 'true';
    removeDuplicateThemeTitle(root);
    initRecentJobs(root);
    initMobileSticky(root);
  }

  function boot(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) init(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
